import { practiceFilters } from "@shared/practiceFilters";
/**
 * Practice setup (brief §practice: `/practice`). Mode toggle, chapters +
 * topics (topic mode only), types, difficulty, count, "Starta". A link can fill
 * the form in (the deep-link contract of pedagogy spec §4.3.9, read by
 * `deepLink.ts`): `?chapters=&topics=` (the progress page's "study this next"
 * recommendations, spec §4.7), `?mode=mixed&chapters=` (mixed mode keeps its
 * plan-first offer), `?types=`, `?count=` and `?primary=1` (topic mode, held
 * to the chosen topics' own questions), so that a suggestion that promises "4
 * numeric problems" actually starts them. The student presses "Starta". A `?set=` link
 * (the exam wrapper's "Vilken princip?" and timed pair) starts by itself instead.
 *
 * Slice C: a bare `/practice` preselects the difficulty for the student's goal (P1-5, ruling 7:
 * G/unknown Lätt+Medel, VG Medel+Svår); a link that names chapters or topics keeps every level.
 * If the preselected levels leave nothing to practise, the page starts without them. `autostart=1`
 * (P1-10) starts at once, but only when the navigation came from inside the app with
 * `state.autostart` (the exam result page); typed or external links (Canvas) just fill the form.
 */
import { invalidPracticeLink } from "./links";
import { InvalidPracticeLink, LectureSetup } from "./LectureSetup";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { difficultyForGoal } from "@shared/userPrefs";
import { useAuth } from "@client/app/auth";
import type { QuestionType } from "@shared/types";
import { useLocalizedText } from "@client/components/Localized";
import {
  Button,
  Card,
  Checkbox,
  Input,
  PageHeader,
  Spinner,
  Tabs,
  useToast,
} from "@client/components/ui";
import { useLang, useT } from "@client/i18n";
import { formatDayShort } from "@client/lib/format";
import { DEFAULT_TIMEZONE, dayKey } from "@shared/time";
import { topicsLock, useExamPeriod, useLectureTimeline } from "@client/features/content/course";
import { lastTaught } from "@shared/courseCalendar";
import { messageForError } from "@client/lib/errors";
import { useStartPractice, useTopicsAndChapters } from "./api";
import { DEFAULT_COUNT, MAX_COUNT, MIN_COUNT, clampCount, readPracticeLink } from "./deepLink";
import { RecentPractice } from "./RecentPractice";
import { useContentOverview } from "@client/features/content/api";
import { ResumeCard } from "./ResumeCard";
import { createSession, saveSession } from "./session";
import { SetStarter } from "./sets/SetStarter";
import type { PracticeMode } from "./types";

const TYPES: QuestionType[] = ["mc", "numeric", "open"];
const DIFFICULTIES = [1, 2, 3] as const;

function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

/**
 * `/practice`: the setup form, or — for a `?set=…` link (step 2.8c) — the exam wrapper's set, which
 * starts itself because the link was the student's "Starta" (`sets/SetStarter.tsx`).
 */
export function PracticePage() {
  const [params] = useSearchParams();
  if (invalidPracticeLink(params)) return <InvalidPracticeLink />;
  if (params.has("lecture"))
    return (
      <LectureSetup
        key={params.toString()}
        course={params.get("course") ?? "HT2026"}
        lecture={params.get("lecture") ?? ""}
      />
    );
  const link = readPracticeLink(params);
  if (link.set) {
    return <SetStarter key={params.toString()} preset={{ ...link, set: link.set }} />;
  }
  return <ManualPracticePage key={params.toString()} />;
}

function ManualPracticePage() {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const localize = useLocalizedText();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { user } = useAuth();

  // Applied once, from the link that brought the student here.
  const [preset] = useState(() => readPracticeLink(searchParams));
  // P1-5: only a bare link gets the goal's levels; a chosen chapter or topic keeps all of them.
  // Derived until the student touches a level, so it follows the profile once that is known.
  const bareLink = preset.chapters.length === 0 && preset.topics.length === 0;
  const goalDifficulty = bareLink ? difficultyForGoal(user?.prefs?.goal) : [];
  const [mode, setMode] = useState<PracticeMode>(preset.mode ?? "mixed");
  const [chapters, setChapters] = useState<number[]>(preset.chapters);
  const [topics, setTopics] = useState<string[]>(preset.topics);
  const [types, setTypes] = useState<QuestionType[]>(preset.types);
  const [difficultyChoice, setDifficultyChoice] = useState<number[] | null>(null);
  const difficulty = difficultyChoice ?? goalDifficulty;
  // P1-6 (ruling 5): "Bara det vi gått igenom" in mixed mode, on by default for a bare link while
  // the course runs and off in Tentaläge. Derived until the student ticks it, like the levels.
  const { timeline } = useLectureTimeline();
  const period = useExamPeriod();
  // After the last lecture every topic is taught, so the box would change nothing: it goes.
  const taughtLecture = timeline && timeline.focus != null ? lastTaught(timeline) : null;
  const taughtDefault = bareLink && taughtLecture !== null && period?.active === false;
  const [taughtChoice, setTaughtChoice] = useState<boolean | null>(null);
  const taughtOn = taughtLecture !== null && (taughtChoice ?? taughtDefault);
  // The count box holds the raw string while it is being typed and is clamped
  // only when it is read (clamping in `onChange` rewrote the first keystroke).
  const [count, setCount] = useState(String(preset.count ?? DEFAULT_COUNT));

  const topicsQuery = useTopicsAndChapters();
  const filters = useMemo(
    () => (topicsQuery.data ? practiceFilters(searchParams, topicsQuery.data) : undefined),
    [searchParams, topicsQuery.data],
  );
  useEffect(() => {
    if (!filters) return;
    setMode(filters.mode);
    setChapters(filters.chapters);
    setTopics(filters.topics);
    setTypes(filters.types);
  }, [filters]);

  // P1-10: an in-app "Öva moment 2, 4, 5" starts at once; the page is replaced in the history, so
  // Back from the session does not start another one.
  const autostart =
    searchParams.get("autostart") === "1" &&
    (location.state as { autostart?: unknown } | null)?.autostart === true;
  const autostarted = useRef(false);
  useEffect(() => {
    if (!autostart || !filters || autostarted.current) return;
    autostarted.current = true;
    void start(
      {
        mode: filters.mode,
        chapters: filters.chapters,
        topics: filters.topics,
        types: filters.types,
      },
      true,
    );
    // `start` is recreated every render; the ref keeps this to one run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autostart, filters]);

  const startMutation = useStartPractice();

  // Per-topic question counts come from the topic browser's overview; without it the list is as before.
  const overview = useContentOverview();
  const topicCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const chapter of overview.data?.chapters ?? [])
      for (const topic of chapter.topics) counts.set(topic.id, topic.counts.total);
    return counts;
  }, [overview.data]);
  function topicLabel(id: string, name: string): string {
    const total = topicCounts.get(id);
    return total === undefined ? name : `${name} (${total})`;
  }

  const visibleTopics = useMemo(() => {
    const all = topicsQuery.data?.topics ?? [];
    return chapters.length > 0 ? all.filter((topic) => chapters.includes(topic.chapter)) : all;
  }, [topicsQuery.data, chapters]);

  const invalid = invalidPracticeLink(searchParams, topicsQuery.data);

  // P1-12: every chosen topic belongs to a lecture not open yet ("Öppnas …" instead of an empty set).
  const { lang } = useLang();
  const lock = mode === "topic" ? topicsLock(topics, topicsQuery.data?.locked) : undefined;
  function lockText(release: { opensAt: number | null }): string {
    return release.opensAt !== null
      ? t("practice.topic.opens", {
          date: formatDayShort(dayKey(release.opensAt, DEFAULT_TIMEZONE), lang),
        })
      : t("practice.topic.closed");
  }

  interface Selection {
    mode: PracticeMode;
    chapters: number[];
    topics: string[];
    types: QuestionType[];
  }

  /** Starts with the form's choices, or with `selection` (autostart, before the form has them). */
  async function start(selection: Selection = { mode, chapters, topics, types }, replace = false) {
    if (invalid || lock || (!topicsQuery.data && !topicsQuery.isError)) return;
    const { mode: m, chapters: ch, topics: tp, types: ty } = selection;
    const body = (levels: number[]) => ({
      mode: m,
      ...(ch.length > 0 ? { chapters: ch } : {}),
      ...(m === "topic" && tp.length > 0 ? { topics: tp } : {}),
      ...(m === "topic" && tp.length > 0 && preset.primaryOnly ? { primaryOnly: true } : {}),
      ...(ty.length > 0 ? { types: ty } : {}),
      ...(levels.length > 0 ? { difficulty: levels } : {}),
      ...(m === "mixed" && taughtOn && taughtLecture ? { taught: taughtLecture.id } : {}),
      limit: clampCount(count),
    });
    try {
      let result = await startMutation.mutateAsync(body(difficulty));
      // The goal's preselected levels must never be why there is nothing to practise.
      const untouchedDefault = difficultyChoice === null && difficulty.length > 0;
      if (result.questions.length === 0 && untouchedDefault) {
        result = await startMutation.mutateAsync(body([]));
      }
      if (result.questions.length === 0) {
        toast.error(
          result.meta.release ? lockText(result.meta.release) : t("practice.setup.noQuestions"),
        );
        return;
      }
      const taught = result.meta.taught;
      if (taught && !taught.applied) {
        // Too little taught yet: the draw used every open topic. Say so as the session opens.
        toast.show(t("practice.taught.fallback", { id: taught.through }));
      }
      saveSession(createSession(m, result.questions, result.plans));
      navigate("/practice/session", replace ? { replace: true } : undefined);
    } catch (error) {
      toast.error(messageForError(error, t));
    }
  }

  if (invalid) return <InvalidPracticeLink />;

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader title={t("practice.nav")} description={t("practice.setup.description")} />
      <ResumeCard />

      {filters && filters.ignoredFilters.length > 0 && (
        <p role="status">
          {t("practice.link.ignored", { filters: filters.ignoredFilters.join(", ") })}
        </p>
      )}
      {topicsQuery.isError && (
        <div role="alert">
          <p>{t("practice.setup.taxonomyError")}</p>
          <Button onClick={() => void topicsQuery.refetch()} loading={topicsQuery.isFetching}>
            {t("common.retry")}
          </Button>
        </div>
      )}
      <Card>
        <Tabs
          ariaLabel={t("practice.setup.mode")}
          value={mode}
          onChange={(value) => {
            setMode(value as PracticeMode);
            if (value === "mixed") setTopics([]);
          }}
          items={[
            { id: "mixed", label: t("practice.mode.mixed") },
            { id: "topic", label: t("practice.mode.topic") },
          ]}
        />

        {mode === "topic" && (
          <div className="mt-4 space-y-4">
            <div>
              <p className="mb-2 text-sm font-medium text-ink-700 dark:text-ink-200">
                {t("practice.setup.chapters")}
              </p>
              <div className="flex flex-wrap gap-2">
                {(topicsQuery.data?.chapters ?? []).map((chapter) => (
                  <label
                    key={chapter.number}
                    className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-ink-300 px-3 py-1 text-sm has-[:checked]:border-accent-600 has-[:checked]:bg-accent-100 dark:border-ink-700 dark:has-[:checked]:border-accent-500 dark:has-[:checked]:bg-accent-900"
                    title={localize(chapter.title)}
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={chapters.includes(chapter.number)}
                      onChange={() => setChapters((prev) => toggle(prev, chapter.number))}
                    />
                    <span className="max-w-[14rem] truncate" title={localize(chapter.title)}>
                      {chapter.number} · {localize(chapter.title)}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <p className="mb-2 text-sm font-medium text-ink-700 dark:text-ink-200">
                {t("practice.setup.topics")}
              </p>
              {topicsQuery.isLoading ? (
                <Spinner size="sm" />
              ) : (
                <div className="max-h-56 space-y-1 overflow-y-auto rounded-lg border border-ink-200 p-2 dark:border-ink-800">
                  {visibleTopics.map((topic) => (
                    <Checkbox
                      key={topic.id}
                      label={topicLabel(topic.id, localize(topic.name))}
                      checked={topics.includes(topic.id)}
                      onChange={() => setTopics((prev) => toggle(prev, topic.id))}
                    />
                  ))}
                  {visibleTopics.length === 0 && (
                    <p className="text-sm text-ink-500 dark:text-ink-400">
                      {t("common.nothingHere")}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {mode === "mixed" && taughtLecture && (
          <div className="mt-4">
            <Checkbox
              label={t("practice.taught.label", { id: taughtLecture.id })}
              checked={taughtOn}
              onChange={() => setTaughtChoice(!taughtOn)}
            />
            <p className="mt-1 text-xs text-ink-600 dark:text-ink-400">
              {period?.active ? t("practice.taught.examMode") : t("practice.taught.hint")}
            </p>
          </div>
        )}

        <div className="mt-4">
          <p className="mb-2 text-sm font-medium text-ink-700 dark:text-ink-200">
            {t("practice.setup.types")}
          </p>
          <div className="flex flex-col gap-2">
            {TYPES.map((type) => (
              <Checkbox
                key={type}
                label={t(`practice.type.${type}`)}
                checked={types.includes(type)}
                onChange={() => setTypes((prev) => toggle(prev, type))}
              />
            ))}
          </div>
        </div>

        <div className="mt-4">
          <p className="mb-2 text-sm font-medium text-ink-700 dark:text-ink-200">
            {t("practice.setup.difficulty")}
          </p>
          <div className="flex gap-4">
            {DIFFICULTIES.map((level) => (
              <Checkbox
                key={level}
                label={t(`practice.difficulty.${level}`)}
                checked={difficulty.includes(level)}
                onChange={() => setDifficultyChoice(toggle(difficulty, level))}
              />
            ))}
          </div>
        </div>

        <div className="mt-4 max-w-[10rem]">
          <Input
            type="number"
            label={t("practice.setup.count")}
            min={MIN_COUNT}
            max={MAX_COUNT}
            value={count}
            onChange={(event) => setCount(event.target.value)}
            onBlur={(event) => setCount(String(clampCount(event.target.value)))}
          />
        </div>

        {lock && (
          <p role="status" className="mt-6 font-medium">
            {lockText(lock)}
          </p>
        )}
        <Button
          className="mt-6"
          onClick={() => void start()}
          loading={startMutation.isPending}
          disabled={Boolean(lock) || (!topicsQuery.data && !topicsQuery.isError)}
          fullWidth
        >
          {t("practice.setup.start")}
        </Button>
      </Card>

      <RecentPractice />
    </div>
  );
}

export default PracticePage;
